"use client"
import { useMemo } from "react"
import {
  DistributionOfPowerBubbles4,
} from "./DistributionOfPowerBubbles4"
import {
  createCellLogoTreatmentFade,
  createTreemapCellStyleFade,
} from "./distribution-of-power-bubbles/powerTreemapStyleFade"

type DistributionOfPowerBubbles4FadeProps = {
  align?: "start" | "end"
  backgroundColor?: string
  className?: string
  height?: number
  maxWidth?: number
  showTransparencyLevels?: boolean
  strokeColor?: string
  strokeWidth?: number
  gapPx?: number
  borderRadiusPx?: number
  iconColor?: string
  // Forwarded to the treemap: sizes each lab icon to this fraction of its
  // cell's smaller dimension (e.g. 0.3 = always 30% of the cell).
  logoSizeFraction?: number
  // Hover-aware treatment (see powerTreemapStyleFade.ts): the hovered country
  // group's cells take this stroke/icon color; every other cell keeps the
  // normal treatment at full strength (no dimming).
  hoveredRegionStrokeColor?: string
  hoveredRegionIconColor?: string
  // Fills the hovered group's cells solid with this color.
  hoveredRegionFillColor?: string
  // Scopes the hoveredRegion* treatments to ONE company at a time: only the
  // exact cell under the pointer takes them, not its whole country group
  // (hoverScope "cell" in powerTreemapStyleFade.ts).
  hoverSingleCompany?: boolean
  // Keeps the frontier distinction legible inside the hovered country's red
  // fill — see hoveredRegionFrontierDistinction in powerTreemapStyleFade.ts.
  hoveredRegionFrontierDistinction?: "hollow-others" | "cream-frontier-outline"
  // Frontier fill mode (see powerTreemapStyleFade.ts): frontier-lab cells
  // fill solid with frontierFillColor at the base border width (no extra
  // 0.5px), their icons in frontierIconColor.
  frontierFillColor?: string
  frontierIconColor?: string
}

// Same whole-world compute treemap as DistributionOfPowerBubbles4, but the
// black squares fade solid -> half-opacity (dashed outline visible) -> empty
// (dashed outline still visible) instead of dithering into dots then outlines.
export const DistributionOfPowerBubbles4Fade = ({
  showTransparencyLevels = false,
  backgroundColor,
  strokeColor,
  strokeWidth,
  iconColor,
  borderRadiusPx,
  hoveredRegionStrokeColor,
  hoveredRegionIconColor,
  hoveredRegionFillColor,
  hoverSingleCompany,
  hoveredRegionFrontierDistinction,
  frontierFillColor,
  frontierIconColor,
  ...props
}: DistributionOfPowerBubbles4FadeProps) => {
  const hoverScope = hoverSingleCompany ? "cell" as const : undefined
  const getCellStyle = useMemo(
    () => createTreemapCellStyleFade({ backgroundColor, strokeColor, strokeWidth, borderRadius: borderRadiusPx, hoveredRegionStrokeColor, hoveredRegionFillColor, hoverScope, hoveredRegionFrontierDistinction, frontierFillColor }),
    [backgroundColor, strokeColor, strokeWidth, borderRadiusPx, hoveredRegionStrokeColor, hoveredRegionFillColor, hoverScope, hoveredRegionFrontierDistinction, frontierFillColor],
  )
  const getLogoTreatment = useMemo(
    () => createCellLogoTreatmentFade({ strokeColor, iconColor, hoveredRegionIconColor, hoverScope, frontierIconColor }),
    [strokeColor, iconColor, hoveredRegionIconColor, hoverScope, frontierIconColor],
  )

  return (
    <DistributionOfPowerBubbles4
      {...props}
      borderRadiusPx={borderRadiusPx}
      showTransparencyLevels={showTransparencyLevels}
      getCellStyle={getCellStyle}
      getLogoTreatment={getLogoTreatment}
    />
  )
}
